import { mergeSortedTabs } from '../utils/sortOptions';
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon, ArrowDownTrayIcon, PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { ALL_DESIGNATIONS, NON_PORTAL_COLORS } from '../constants/staffDesignations';
import { useSchoolModules } from '../hooks/useSchoolModules';

// ── Date helpers — local calendar date, not UTC (toISOString shifts the date
// backward during Pakistan's midnight–5am window since it reports the UTC day) ──
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const curMonthStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };

// ── Export helpers ────────────────────────────────────────────────────────────
const exportDownloadCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape  = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    return s.includes(',') || s.includes('"') ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv  = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename + '.csv'; a.click();
  URL.revokeObjectURL(url);
};

const exportData = async (rows, filename, format) => {
  if (!rows || !rows.length) return;
  if (format === 'csv') { exportDownloadCSV(rows, filename); return; }
  try {
    const XLSX = await import('xlsx');
    const ws   = XLSX.utils.json_to_sheet(rows);
    const wb   = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename + '.xlsx');
  } catch { exportDownloadCSV(rows, filename); }
};
// ─────────────────────────────────────────────────────────────────────────────

const BASE_PORTAL_TABS = [
  { key: 'teacher',     label: 'Teachers',     color: '#3b6ef8' },
  { key: 'coordinator', label: 'Coordinators', color: '#8b5cf6' },
  { key: 'finance',     label: 'Finance',      color: '#0d9488' },
];
const STATUS_OPTS  = ['present','absent','on-leave','holiday','half-day'];
const STATUS_COLOR = { present:'#10c98e', absent:'#ef4444', 'on-leave':'#f59e0b', holiday:'#8b5cf6', 'half-day':'#3b6ef8' };
const STATUS_BG    = { present:'#ecfdf5', absent:'#fef2f2', 'on-leave':'#fffbeb', holiday:'#f3f0ff', 'half-day':'#eef2ff' };
const MONTHS  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const initials = n => (n||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';
const fmt12h = (t) => {
  if (!t) return '—';
  const [h,m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,'0')} ${period}`;
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .fsa * { box-sizing:border-box; }
  .fsa { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .fsa-title { font-size:1.4rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:3px; }
  .fsa-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }
  .role-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .role-tab  { padding:7px 16px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .role-tab.active { color:white; border-color:transparent; }
  .fsa-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .fsa-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .fsa-date:focus { border-color:#0d9488; }
  .fsa-search-wrap { position:relative; flex:1; max-width:280px; }
  .fsa-search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); width:14px; height:14px; stroke:#9094a8; pointer-events:none; }
  .fsa-search { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 32px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .fsa-search:focus { border-color:#0d9488; }
  .view-tabs { display:flex; gap:5px; }
  .view-btn  { padding:7px 14px; border-radius:9px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .view-btn.active { background:#0d9488; color:white; border-color:#0d9488; }
  .btn-request-all { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border:none; border-radius:10px; background:#0d9488; color:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; }
  .btn-request-all svg { width:14px; height:14px; stroke-width:2.5; }
  .btn-request-all:disabled { opacity:.5; cursor:not-allowed; }
  .export-group { display:flex; gap:5px; margin-left:auto; align-items:center; }
  .btn-export { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .btn-export svg { width:14px; height:14px; stroke-width:2; }
  .btn-export.csv:hover  { background:#ecfdf5; color:#0d9488; border-color:#0d9488; }
  .btn-export.xlsx:hover { background:#eef2ff; color:#3b6ef8; border-color:#3b6ef8; }
  /* Panel */
  .fsa-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .fsa-tbl-wrap { overflow-x:auto; }
  .fsa-tbl { width:100%; border-collapse:collapse; }
  .fsa-tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .fsa-tbl th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .fsa-tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .1s; }
  .fsa-tbl tbody tr:last-child { border-bottom:none; }
  .fsa-tbl tbody tr:hover { background:#fafbff; }
  .fsa-tbl td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .fsa-avatar { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; color:white; flex-shrink:0; }
  .status-pill { display:inline-flex; font-size:.67rem; font-weight:700; padding:3px 10px; border-radius:99px; white-space:nowrap; }
  .sp-present  { background:#ecfdf5; color:#10c98e; }
  .sp-absent   { background:#fef2f2; color:#ef4444; }
  .sp-on-leave { background:#fffbeb; color:#f59e0b; }
  .sp-holiday  { background:#f3f0ff; color:#8b5cf6; }
  .sp-half-day { background:#eef2ff; color:#3b6ef8; }
  .sp-empty    { background:#f0f2f7; color:#c0c5dc; }
  .status-sel { font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; padding:4px 8px; border-radius:8px; border:1.5px solid; cursor:pointer; outline:none; }
  .time-input { font-family:'JetBrains Mono',monospace; font-size:.74rem; padding:4px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; outline:none; width:90px; }
  .time-input:focus { border-color:#0d9488; background:white; }
  .late-input { font-family:'JetBrains Mono',monospace; font-size:.74rem; padding:4px 6px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; outline:none; width:60px; }
  .btn-request-row { display:inline-flex; align-items:center; gap:4px; padding:5px 12px; border:none; border-radius:8px; background:#0d9488; color:white; font-family:'Sora',sans-serif; font-size:.72rem; font-weight:600; cursor:pointer; white-space:nowrap; }
  .btn-request-row svg { width:12px; height:12px; stroke-width:2.5; }
  .btn-request-row:disabled { opacity:.5; cursor:not-allowed; }
  .requested-badge { font-size:.68rem; font-weight:600; color:#0d9488; background:#f0fdfa; padding:4px 10px; border-radius:8px; white-space:nowrap; }
  .own-row-badge { font-size:.68rem; font-weight:600; color:#9094a8; font-style:italic; padding:4px 8px; }
  .stat-pill { font-size:.68rem; font-weight:700; padding:3px 10px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }
  /* Yearly */
  .yr-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem; }
  .yr-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; }
  .yr-card-name { font-size:.88rem; font-weight:700; color:#0f1123; margin-bottom:.8rem; display:flex; align-items:center; gap:9px; }
  .yr-months { display:grid; grid-template-columns:repeat(6,1fr); gap:4px; }
  .yr-month { text-align:center; }
  .yr-month-lbl { font-size:.58rem; font-weight:600; color:#9094a8; text-transform:uppercase; margin-bottom:3px; }
  .yr-month-bar { height:32px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:.62rem; font-weight:700; }
  .yr-totals { display:flex; gap:8px; flex-wrap:wrap; margin-top:.8rem; padding-top:.7rem; border-top:1px solid #f0f2f7; }
  .yr-total { font-size:.68rem; font-weight:600; }
  /* States */
  .fsa-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .fsa-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .fsa-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  /* Approval notice */
  .approval-notice { display:flex; align-items:center; gap:8px; padding:10px 16px; background:#f0fdfa; border:1px solid #99f6e4; border-radius:10px; font-size:.76rem; color:#0f766e; font-weight:600; margin-bottom:1rem; }

  /* ── Mobile cards ── */
  .fsa-cards { display:none; padding:10px; }
  .fsa-card  { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .fsa-card-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; flex-wrap:wrap; }
  .fsa-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .fsa-card-row:last-child { border-bottom:none; }
  .fsa-card-lbl { color:#9094a8; font-weight:600; white-space:nowrap; }
  .fsa-card-actions { display:flex; gap:6px; margin-top:8px; flex-wrap:wrap; align-items:center; }
  @media (max-width:768px) { .fsa-title { font-size:1.1rem; } .fsa-tbl-wrap { display:none !important; } .fsa-cards { display:block; } }
`;

export default function FinanceStaffAttendance() {
  const { libraryEnabled, hostelEnabled } = useSchoolModules();
  const PORTAL_TABS = [
    ...BASE_PORTAL_TABS,
    ...(libraryEnabled ? [{ key: 'librarian',     label: 'Librarians',   color: '#f59e0b' }] : []),
    ...(hostelEnabled  ? [{ key: 'hostel_member', label: 'Hostel Staff', color: '#ec4899' }] : []),
  ];
  const PORTAL_ROLES = PORTAL_TABS.map(t => t.key);
  const [activeRole, setActiveRole] = useState('');
  const [view,       setView]       = useState('daily');
  const [date,       setDate]       = useState(todayStr());
  const [month,      setMonth]      = useState(curMonthStr());
  const [year,       setYear]       = useState(new Date().getFullYear());
  const [search,     setSearch]     = useState('');
  const [staff,      setStaff]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [forms,      setForms]      = useState({});
  const [requesting, setRequesting] = useState({});
  const [requested,  setRequested]  = useState({});
  const [myUserId,   setMyUserId]   = useState(null);
  const [startTime,  setStartTime]  = useState('09:00');

  const isPortal  = PORTAL_ROLES.includes(activeRole);
  const isAll     = activeRole === '';
  const portalTab = PORTAL_TABS.find(t => t.key === activeRole);
  const tabColor  = portalTab?.color || NON_PORTAL_COLORS[ALL_DESIGNATIONS.indexOf(activeRole) % NON_PORTAL_COLORS.length] || '#9094a8';
  const tab       = isAll ? { key:'', label:'All', color:'#6b7194' } : portalTab || { key: activeRole, label: activeRole, color: tabColor };
  const isFutureDate = view === 'daily' && date > todayStr();
  const availableStatusOpts = isFutureDate ? ['on-leave'] : STATUS_OPTS;

  useEffect(() => {
    api.get('/finance/profile').then(r => setMyUserId(String(r.data?._id||''))).catch(()=>{});
    api.get('/finance/holidays/settings').then(r => setStartTime(r.data?.startTime || '09:00')).catch(()=>{});
  }, []);

  // Auto-calculate late minutes from check-in time, same formula as self check-in
  const calcLateMinutes = (checkIn) => {
    if (!checkIn) return 0;
    const [sh, sm] = startTime.split(':').map(Number);
    const [ch, cm] = checkIn.split(':').map(Number);
    const diff = (ch*60+cm) - (sh*60+sm);
    return diff > 0 ? diff : 0;
  };

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setRequested({});
    try {
      if (view === 'daily') {
        const { data } = await api.get(`/finance/staff-attendance?role=${activeRole}&date=${date}`);
        const list = Array.isArray(data) ? data : [];
        setStaff(list);
        const f = {};
        list.forEach(s => {
          f[s._id] = {
            status:      s.attendance?.status || '',
            checkIn:     s.attendance?.checkIn  ? new Date(s.attendance.checkIn).toTimeString().slice(0,5)  : '',
            checkOut:    s.attendance?.checkOut ? new Date(s.attendance.checkOut).toTimeString().slice(0,5) : '',
            lateMinutes: s.attendance?.lateMinutes || 0,
          };
        });
        setForms(f);
      } else if (view === 'monthly') {
        const { data } = await api.get(`/finance/staff-attendance/monthly?month=${month}&role=${activeRole}`);
        setStaff(Array.isArray(data) ? data : []);
      } else {
        // Yearly — 12 parallel monthly calls
        const base     = await api.get(`/finance/staff-attendance?role=${activeRole}&date=${year}-01-01`);
        const baseList = (Array.isArray(base.data) ? base.data : []).map(s => ({
          _id: s._id, name: s.name, employeeId: s.employeeId, role: s.role, months: {},
        }));
        const monthlyData = await Promise.all(
          Array.from({ length: 12 }, (_, i) => {
            const m = String(i+1).padStart(2,'0');
            return api.get(`/finance/staff-attendance/monthly?month=${year}-${m}&role=${activeRole}`)
              .then(r => ({ month: i+1, data: Array.isArray(r.data) ? r.data : [] }))
              .catch(() => ({ month: i+1, data: [] }));
          })
        );
        baseList.forEach(s => {
          monthlyData.forEach(({ month: m, data }) => {
            const found = data.find(d => d._id?.toString() === s._id?.toString());
            s.months[m] = found || { present:0, absent:0, onLeave:0, lateCount:0, totalLateMinutes:0 };
          });
        });
        setStaff(baseList);
      }
    } catch { toast.error('Failed to load'); }
    finally   { setLoading(false); }
  }, [activeRole, view, date, month, year]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const setForm = (id, key, val) =>
    setForms(p => ({ ...p, [id]: { ...p[id], [key]: val } }));

  const handleCheckInChange = (id, val) =>
    setForms(p => ({ ...p, [id]: { ...p[id], checkIn: val, lateMinutes: calcLateMinutes(val) } }));

  const buildPayload = (s) => {
    const f = forms[s._id] || {};
    return {
      staffId: s._id, isPortal: s.isPortal ?? isPortal, role: s.role || activeRole, date,
      status:      f.status,
      checkIn:     f.checkIn  ? `${date}T${f.checkIn}:00`  : undefined,
      checkOut:    f.checkOut ? `${date}T${f.checkOut}:00` : undefined,
      lateMinutes: Number(f.lateMinutes) || 0,
      staffName:   s.name,
      employeeId:  s.employeeId || '',
    };
  };

  const requestSingle = async (s) => {
    const f = forms[s._id] || {};
    if (!f.status) return toast.error('Select a status first');
    setRequesting(p => ({ ...p, [s._id]: true }));
    try {
      await api.post('/finance/requests', {
        type: 'staff_attendance_mark',
        payload: buildPayload(s),
        note: `Attendance for ${s.name} on ${date}`,
      });
      toast.success('Request submitted for admin approval');
      setRequested(p => ({ ...p, [s._id]: true }));
    } catch { toast.error('Failed to submit request'); }
    finally { setRequesting(p => ({ ...p, [s._id]: false })); }
  };

  const requestAll = async () => {
    const targets = staff.filter(s =>
      forms[s._id]?.status && String(s._id) !== String(myUserId) && !requested[s._id]
    );
    if (!targets.length) return toast.error('No attendance marked');
    try {
      await Promise.all(targets.map(s => api.post('/finance/requests', {
        type: 'staff_attendance_mark',
        payload: buildPayload(s),
        note: `Attendance for ${s.name} on ${date}`,
      })));
      toast.success(`${targets.length} request${targets.length>1?'s':''} submitted for admin approval`);
      setRequested(p => { const n = { ...p }; targets.forEach(s => { n[s._id] = true; }); return n; });
    } catch { toast.error('Some requests failed to submit'); }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = (fmt) => {
    if (!staff.length) return toast.error('No data to export');
    const roleName = tab?.label || activeRole;
    let rows = [];

    if (view === 'daily') {
      rows = staff.map(s => ({
        Name:         s.name,
        'Employee ID':s.employeeId || '',
        Role:         s.role || activeRole,
        Date:         date,
        Status:       s.attendance?.status || '',
        'Check In':   s.attendance?.checkIn  ? new Date(s.attendance.checkIn).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '',
        'Check Out':  s.attendance?.checkOut ? new Date(s.attendance.checkOut).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '',
        'Late (min)': s.attendance?.lateMinutes || 0,
      }));
      exportData(rows, `Staff_Attendance_${roleName}_${date}`, fmt);
    } else if (view === 'monthly') {
      rows = staff.map(s => ({
        Name:              s.name,
        'Employee ID':     s.employeeId || '',
        Role:              s.role || activeRole,
        Month:             month,
        Present:           s.present || 0,
        Absent:            s.absent  || 0,
        'On Leave':        s.onLeave || 0,
        'Late Days':       s.lateCount || 0,
        'Total Late (min)':s.totalLateMinutes || 0,
      }));
      exportData(rows, `Staff_Attendance_${roleName}_${month}`, fmt);
    } else {
      staff.forEach(s => {
        Array.from({ length:12 }, (_, i) => {
          const m = s.months[i+1] || {};
          rows.push({
            Name:              s.name,
            'Employee ID':     s.employeeId || '',
            Role:              s.role || activeRole,
            Month:             `${year}-${String(i+1).padStart(2,'0')}`,
            Present:           m.present || 0,
            Absent:            m.absent  || 0,
            'On Leave':        m.onLeave || 0,
            'Late Days':       m.lateCount || 0,
            'Total Late (min)':m.totalLateMinutes || 0,
          });
        });
      });
      exportData(rows, `Staff_Attendance_${roleName}_${year}`, fmt);
    }
  };

  const exportEmployee = (s, fmt) => {
    if (view === 'monthly') {
      exportData([{
        Name: s.name, 'Employee ID': s.employeeId||'', Role: s.role||activeRole, Month: month,
        Present: s.present||0, Absent: s.absent||0, 'On Leave': s.onLeave||0,
        'Late Days': s.lateCount||0, 'Total Late (min)': s.totalLateMinutes||0,
      }], `Attendance_${s.name}_${month}`, fmt);
    } else if (view === 'yearly') {
      const rows = Array.from({ length:12 }, (_, i) => {
        const m = s.months?.[i+1] || {};
        const total = (m.present||0)+(m.absent||0)+(m.onLeave||0);
        return {
          Name: s.name, 'Employee ID': s.employeeId||'',
          Month: `${year}-${String(i+1).padStart(2,'0')}`,
          Present: m.present||0, Absent: m.absent||0, 'On Leave': m.onLeave||0,
          'Late Days': m.lateCount||0, 'Total Late (min)': m.totalLateMinutes||0,
          'Attendance %': total>0 ? `${Math.round(((m.present||0)/total)*100)}%` : '0%',
        };
      });
      exportData(rows, `Attendance_${s.name}_${year}`, fmt);
    } else {
      const att = s.attendance || {};
      exportData([{
        Name: s.name, 'Employee ID': s.employeeId||'', Role: s.role||activeRole, Date: date,
        Status: att.status||'',
        'Check In':  att.checkIn  ? new Date(att.checkIn).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '',
        'Check Out': att.checkOut ? new Date(att.checkOut).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '',
        'Late (min)': att.lateMinutes||0,
      }], `Attendance_${s.name}_${date}`, fmt);
    }
  };

  const filtered = staff.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase())
  );

  const isOwnRow = (s) => myUserId && String(s._id) === String(myUserId);

  return (
    <div className="fsa"><style>{css}</style>
      <div className="fsa-title">Staff Attendance</div>
      <div className="fsa-sub">Mark attendance for all staff roles — every change is sent to the admin for approval.</div>

      {/* Approval notice */}
      <div className="approval-notice">
        📨 Attendance marked here is submitted as a request — it only takes effect once the admin approves it.
      </div>

      {/* Role tabs */}
      <div className="role-tabs">
        <button className={`role-tab ${isAll?'active':''}`}
          style={isAll ? { background:'#6b7194' } : {}}
          onClick={() => { setActiveRole(''); setSearch(''); }}>
          All
        </button>
        {mergeSortedTabs(PORTAL_TABS, ALL_DESIGNATIONS, NON_PORTAL_COLORS).map(t => (
          <button key={t.key}
            className={`role-tab ${activeRole===t.key?'active':''}`}
            style={activeRole===t.key ? { background:t.color } : {}}
            onClick={() => { setActiveRole(t.key); setSearch(''); }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="fsa-toolbar">
        {view === 'daily'
          ? <input type="date"   className="fsa-date" value={date}  onChange={e => setDate(e.target.value)} />
          : view === 'monthly'
          ? <input type="month"  className="fsa-date" value={month} onChange={e => setMonth(e.target.value)} />
          : <input type="number" className="fsa-date" value={year} min="2020" max="2099"
              onChange={e => setYear(Number(e.target.value))} style={{width:100}} />
        }
        <div className="fsa-search-wrap">
          <MagnifyingGlassIcon />
          <input className="fsa-search" placeholder={`Search ${tab?.label}…`}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="view-tabs">
          {['daily','monthly','yearly'].map(v => (
            <button key={v} className={`view-btn ${view===v?'active':''}`} onClick={() => setView(v)}>
              {v.charAt(0).toUpperCase()+v.slice(1)}
            </button>
          ))}
        </div>
        {view === 'daily' && (
          <button className="btn-request-all" onClick={requestAll}>
            <PaperAirplaneIcon />Request All
          </button>
        )}
        <div className="export-group">
          <button className="btn-export csv"  onClick={() => handleExport('csv')}><ArrowDownTrayIcon />CSV</button>
          <button className="btn-export xlsx" onClick={() => handleExport('xlsx')}><ArrowDownTrayIcon />XLSX</button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="fsa-loading"><div className="fsa-spin"/>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="fsa-panel"><div className="fsa-empty">No {tab?.label} found.</div></div>

      ) : view === 'daily' ? (
        /* ── DAILY VIEW ── */
        <div className="fsa-panel">
          <div className="fsa-tbl-wrap">
            <table className="fsa-tbl">
              <thead><tr>
                <th>Name</th>{isAll && <th>Role</th>}<th>Status</th><th>Check In</th><th>Check Out</th><th>Late (min)</th><th>Action</th>
              </tr></thead>
              <tbody>
                {filtered.map(s => {
                  const f    = forms[s._id] || {};
                  const sc   = STATUS_COLOR[f.status]||'#9094a8';
                  const sb   = STATUS_BG[f.status]||'#f0f2f7';
                  const own  = isOwnRow(s);
                  const reqd = requested[s._id];
                  return (
                    <tr key={s._id} style={own ? { background:'#fafbff', opacity:.75 } : {}}>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                          <div className="fsa-avatar" style={{background:`linear-gradient(135deg,${tab?.color},${tab?.color}99)`}}>
                            {initials(s.name)}
                          </div>
                          <div>
                            <div style={{fontWeight:600,color:'#0f1123'}}>{s.name}</div>
                            {s.employeeId && <div style={{fontSize:'.68rem',color:'#9094a8'}}>{s.employeeId}</div>}
                            {own && <div style={{fontSize:'.64rem',color:'#0d9488',fontWeight:600}}>👤 You</div>}
                          </div>
                        </div>
                      </td>
                      {isAll && <td><span style={{fontSize:'.72rem',fontWeight:600,textTransform:'capitalize',color:'#6b7194'}}>{s.role}</span></td>}
                      <td>
                        {own
                          ? <span className={f.status ? `status-pill sp-${f.status.replace(' ','-')}` : 'status-pill sp-empty'}>{f.status||'—'}</span>
                          : <select className="status-sel" style={{background:sb,color:sc,borderColor:sc}}
                              value={f.status||''} onChange={e=>setForm(s._id,'status',e.target.value)}>
                              <option value="">— select —</option>
                              {availableStatusOpts.map(st=><option key={st} value={st}>{st}</option>)}
                            </select>
                        }
                      </td>
                      <td>
                        {own
                          ? <span className="mono">{fmt12h(f.checkIn)}</span>
                          : (f.status==='present'||f.status==='half-day')
                          ? <div style={{display:'flex',alignItems:'center',gap:6}}>
                              <input type="time" className="time-input" value={f.checkIn||''} onChange={e=>handleCheckInChange(s._id,e.target.value)} />
                              {f.checkIn && <span style={{fontSize:'.66rem',color:'#9094a8',whiteSpace:'nowrap'}}>{fmt12h(f.checkIn)}</span>}
                            </div>
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                      </td>
                      <td>
                        {own
                          ? <span className="mono">{fmt12h(f.checkOut)}</span>
                          : (f.status==='present'||f.status==='half-day')
                          ? <div style={{display:'flex',alignItems:'center',gap:6}}>
                              <input type="time" className="time-input" value={f.checkOut||''} onChange={e=>setForm(s._id,'checkOut',e.target.value)} />
                              {f.checkOut && <span style={{fontSize:'.66rem',color:'#9094a8',whiteSpace:'nowrap'}}>{fmt12h(f.checkOut)}</span>}
                            </div>
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                      </td>
                      <td>
                        {own
                          ? <span className="mono" style={{color:(f.lateMinutes||0)>0?'#f59e0b':'#c0c5dc'}}>{(f.lateMinutes||0)>0?`${f.lateMinutes}m`:'—'}</span>
                          : f.status==='present'
                          ? <input type="number" min="0" className="late-input" value={f.lateMinutes||0} onChange={e=>setForm(s._id,'lateMinutes',e.target.value)} />
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                      </td>
                      <td>
                        <div style={{display:'flex',gap:4,alignItems:'center'}}>
                          {own
                            ? <span className="own-row-badge">Your record — use My Attendance</span>
                            : reqd
                            ? <span className="requested-badge">✓ Requested</span>
                            : <button className="btn-request-row" disabled={requesting[s._id]} onClick={()=>requestSingle(s)}>
                                <PaperAirplaneIcon />{requesting[s._id]?'…':'Request'}
                              </button>
                          }
                          <button onClick={()=>exportEmployee(s,'csv')}
                            style={{padding:'5px 8px',border:'1.5px solid #e0e4f0',borderRadius:7,background:'white',cursor:'pointer',fontSize:'.68rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>
                            CSV
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="fsa-cards">
            {filtered.map(s => {
              const f    = forms[s._id] || {};
              const sc   = STATUS_COLOR[f.status]||'#9094a8';
              const sb   = STATUS_BG[f.status]||'#f0f2f7';
              const own  = isOwnRow(s);
              const reqd = requested[s._id];
              return (
                <div key={s._id} className="fsa-card" style={own ? { opacity:.75 } : {}}>
                  <div className="fsa-card-top">
                    <div style={{display:'flex',alignItems:'center',gap:9}}>
                      <div className="fsa-avatar" style={{background:`linear-gradient(135deg,${tab?.color},${tab?.color}99)`}}>
                        {initials(s.name)}
                      </div>
                      <div>
                        <div style={{fontWeight:600,color:'#0f1123'}}>{s.name}</div>
                        {s.employeeId && <div style={{fontSize:'.68rem',color:'#9094a8'}}>{s.employeeId}</div>}
                        {own && <div style={{fontSize:'.64rem',color:'#0d9488',fontWeight:600}}>👤 You</div>}
                      </div>
                    </div>
                    {own
                      ? <span className={f.status ? `status-pill sp-${f.status.replace(' ','-')}` : 'status-pill sp-empty'}>{f.status||'—'}</span>
                      : <select className="status-sel" style={{background:sb,color:sc,borderColor:sc}}
                          value={f.status||''} onChange={e=>setForm(s._id,'status',e.target.value)}>
                          <option value="">— select —</option>
                          {availableStatusOpts.map(st=><option key={st} value={st}>{st}</option>)}
                        </select>
                    }
                  </div>
                  <div className="fsa-card-row">
                    <span className="fsa-card-lbl">Check In</span>
                    {own
                      ? <span className="mono">{fmt12h(f.checkIn)}</span>
                      : (f.status==='present'||f.status==='half-day')
                      ? <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <input type="time" className="time-input" value={f.checkIn||''} onChange={e=>handleCheckInChange(s._id,e.target.value)} />
                          {f.checkIn && <span style={{fontSize:'.66rem',color:'#9094a8',whiteSpace:'nowrap'}}>{fmt12h(f.checkIn)}</span>}
                        </div>
                      : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                  </div>
                  <div className="fsa-card-row">
                    <span className="fsa-card-lbl">Check Out</span>
                    {own
                      ? <span className="mono">{fmt12h(f.checkOut)}</span>
                      : (f.status==='present'||f.status==='half-day')
                      ? <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <input type="time" className="time-input" value={f.checkOut||''} onChange={e=>setForm(s._id,'checkOut',e.target.value)} />
                          {f.checkOut && <span style={{fontSize:'.66rem',color:'#9094a8',whiteSpace:'nowrap'}}>{fmt12h(f.checkOut)}</span>}
                        </div>
                      : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                  </div>
                  <div className="fsa-card-row">
                    <span className="fsa-card-lbl">Late (min)</span>
                    {own
                      ? <span className="mono" style={{color:(f.lateMinutes||0)>0?'#f59e0b':'#c0c5dc'}}>{(f.lateMinutes||0)>0?`${f.lateMinutes}m`:'—'}</span>
                      : f.status==='present'
                      ? <input type="number" min="0" className="late-input" value={f.lateMinutes||0} onChange={e=>setForm(s._id,'lateMinutes',e.target.value)} />
                      : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                  </div>
                  <div className="fsa-card-actions">
                    {own
                      ? <span className="own-row-badge">Your record — use My Attendance</span>
                      : reqd
                      ? <span className="requested-badge">✓ Requested</span>
                      : <button className="btn-request-row" disabled={requesting[s._id]} onClick={()=>requestSingle(s)}>
                          <PaperAirplaneIcon />{requesting[s._id]?'…':'Request'}
                        </button>
                    }
                    <button onClick={()=>exportEmployee(s,'csv')}
                      style={{padding:'5px 8px',border:'1.5px solid #e0e4f0',borderRadius:7,background:'white',cursor:'pointer',fontSize:'.68rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>
                      CSV
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      ) : view === 'monthly' ? (
        /* ── MONTHLY VIEW ── */
        <div className="fsa-panel">
          <div className="fsa-tbl-wrap">
            <table className="fsa-tbl">
              <thead><tr>
                <th>Name</th><th>Present</th><th>Absent</th><th>On Leave</th><th>Late Days</th><th>Late Mins</th><th>Export</th>
              </tr></thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s._id}>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div className="fsa-avatar" style={{background:`linear-gradient(135deg,${tab?.color},${tab?.color}99)`}}>
                          {initials(s.name)}
                        </div>
                        <div style={{fontWeight:600,color:'#0f1123'}}>{s.name}</div>
                      </div>
                    </td>
                    <td><span className="stat-pill" style={{background:'#ecfdf5',color:'#10c98e'}}>{s.present||0}</span></td>
                    <td><span className="stat-pill" style={{background:'#fef2f2',color:'#ef4444'}}>{s.absent||0}</span></td>
                    <td><span className="stat-pill" style={{background:'#fffbeb',color:'#f59e0b'}}>{s.onLeave||0}</span></td>
                    <td style={{fontSize:'.78rem',color:'#6b7194'}}>{s.lateCount||0}</td>
                    <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#ef4444'}}>{s.totalLateMinutes||0} min</td>
                    <td>
                      <div style={{display:'flex',gap:3}}>
                        <button onClick={()=>exportEmployee(s,'csv')} style={{padding:'4px 7px',border:'1.5px solid #e0e4f0',borderRadius:6,background:'white',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>CSV</button>
                        <button onClick={()=>exportEmployee(s,'xlsx')} style={{padding:'4px 7px',border:'1.5px solid #3b6ef8',borderRadius:6,background:'#eef2ff',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#3b6ef8',fontFamily:'Sora'}}>XLSX</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="fsa-cards">
            {filtered.map(s => (
              <div key={s._id} className="fsa-card">
                <div className="fsa-card-top">
                  <div style={{display:'flex',alignItems:'center',gap:9}}>
                    <div className="fsa-avatar" style={{background:`linear-gradient(135deg,${tab?.color},${tab?.color}99)`}}>
                      {initials(s.name)}
                    </div>
                    <div style={{fontWeight:600,color:'#0f1123'}}>{s.name}</div>
                  </div>
                </div>
                <div className="fsa-card-row"><span className="fsa-card-lbl">Present</span><span className="stat-pill" style={{background:'#ecfdf5',color:'#10c98e'}}>{s.present||0}</span></div>
                <div className="fsa-card-row"><span className="fsa-card-lbl">Absent</span><span className="stat-pill" style={{background:'#fef2f2',color:'#ef4444'}}>{s.absent||0}</span></div>
                <div className="fsa-card-row"><span className="fsa-card-lbl">On Leave</span><span className="stat-pill" style={{background:'#fffbeb',color:'#f59e0b'}}>{s.onLeave||0}</span></div>
                <div className="fsa-card-row"><span className="fsa-card-lbl">Late Days</span><span style={{fontSize:'.78rem',color:'#6b7194'}}>{s.lateCount||0}</span></div>
                <div className="fsa-card-row"><span className="fsa-card-lbl">Late Mins</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#ef4444'}}>{s.totalLateMinutes||0} min</span></div>
                <div className="fsa-card-actions">
                  <button onClick={()=>exportEmployee(s,'csv')} style={{padding:'4px 7px',border:'1.5px solid #e0e4f0',borderRadius:6,background:'white',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>CSV</button>
                  <button onClick={()=>exportEmployee(s,'xlsx')} style={{padding:'4px 7px',border:'1.5px solid #3b6ef8',borderRadius:6,background:'#eef2ff',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#3b6ef8',fontFamily:'Sora'}}>XLSX</button>
                </div>
              </div>
            ))}
          </div>
        </div>

      ) : (
        /* ── YEARLY VIEW ── */
        <div className="yr-grid">
          {filtered.map(s => {
            const totals = Object.values(s.months||{}).reduce((acc,m) => ({
              present: acc.present + (m.present||0),
              absent:  acc.absent  + (m.absent||0),
              onLeave: acc.onLeave + (m.onLeave||0),
              lateMin: acc.lateMin + (m.totalLateMinutes||0),
            }), { present:0, absent:0, onLeave:0, lateMin:0 });
            return (
              <div key={s._id} className="yr-card">
                <div className="yr-card-name">
                  <div className="fsa-avatar" style={{width:30,height:30,fontSize:'.66rem',background:`linear-gradient(135deg,${tab?.color},${tab?.color}99)`}}>
                    {initials(s.name)}
                  </div>
                  {s.name}
                  {s.employeeId && <span style={{fontSize:'.68rem',color:'#9094a8',marginLeft:'auto'}}>{s.employeeId}</span>}
                </div>
                <div className="yr-months">
                  {MONTHS.map((lbl, i) => {
                    const m     = s.months?.[i+1] || {};
                    const total = (m.present||0)+(m.absent||0)+(m.onLeave||0);
                    const pct   = total > 0 ? Math.round(((m.present||0)/total)*100) : 0;
                    const bg    = pct>=90?'#ecfdf5':pct>=70?'#fffbeb':pct>0?'#fef2f2':'#f0f2f7';
                    const col   = pct>=90?'#10c98e':pct>=70?'#f59e0b':pct>0?'#ef4444':'#c0c5dc';
                    return (
                      <div key={lbl} className="yr-month">
                        <div className="yr-month-lbl">{lbl}</div>
                        <div className="yr-month-bar" style={{background:bg,color:col}}>
                          {total > 0 ? `${pct}%` : '—'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="yr-totals">
                  <span className="yr-total" style={{color:'#10c98e'}}>✓ {totals.present} present</span>
                  <span className="yr-total" style={{color:'#ef4444'}}>✗ {totals.absent} absent</span>
                  <span className="yr-total" style={{color:'#f59e0b'}}>◑ {totals.onLeave} leave</span>
                  <span className="yr-total" style={{color:'#9094a8'}}>⏱ {totals.lateMin} late min</span>
                  <span style={{marginLeft:'auto',display:'flex',gap:3}}>
                    <button onClick={()=>exportEmployee(s,'csv')} style={{padding:'3px 7px',border:'1.5px solid #e0e4f0',borderRadius:6,background:'white',cursor:'pointer',fontSize:'.64rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>CSV</button>
                    <button onClick={()=>exportEmployee(s,'xlsx')} style={{padding:'3px 7px',border:'1.5px solid #3b6ef8',borderRadius:6,background:'#eef2ff',cursor:'pointer',fontSize:'.64rem',fontWeight:600,color:'#3b6ef8',fontFamily:'Sora'}}>XLSX</button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
